import {useQuery} from '@apollo/client';
import {DatePicker, Form, Spin} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  UserOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  GET_SIMULATOR_STATS,
  GET_SIMULATOR_SUBSCRIPTION_MONTHLY_BREAKDOWN,
} from '../resolvers';
import {GET_SIMULATOR_RESTART_PURCHASES_STATS} from 'pages/simulatorConfig/resolvers';
import styles from '../analytics.module.scss';
import {useMemo, useState} from 'react';
import SubmitButton from 'components/Startup/components/SubmitButton';
import moment from 'moment';
import {SimulatorSubscriptionsTable} from './SimulatorSubscriptionsTable';

export const DabaSimulatorOverviewPage = () => {
  const [form] = Form.useForm();
  const [selectedYear, setSelectedYear] = useState(moment());
  const [appliedFilters, setAppliedFilters] = useState(null);

  const {
    data,
    loading,
    refetch: refetchStats,
  } = useQuery(GET_SIMULATOR_STATS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: restartStatsData,
    loading: restartStatsLoading,
    refetch: refetchRestartStats,
  } = useQuery(GET_SIMULATOR_RESTART_PURCHASES_STATS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {data: monthlyData, loading: monthlyLoading} = useQuery(
    GET_SIMULATOR_SUBSCRIPTION_MONTHLY_BREAKDOWN,
    {
      variables: {year: Number(selectedYear.format('YYYY'))},
      fetchPolicy: 'no-cache',
    },
  );

  const onFinish = values => {
    const {startDate, endDate} = values || {};
    const filters = {
      startDate: startDate ? moment(startDate).format() : null,
      endDate: endDate ? moment(endDate).format() : null,
    };
    setAppliedFilters(filters);
    refetchStats({filters});
    refetchRestartStats({filters});
  };

  const stats = useMemo(() => {
    const d = data?.getSimulatorStatistics?.data;
    return d?.__typename === 'SimulatorStatisticsData' ? d : {};
  }, [data]);

  /** API returns totalRevenue as CurrencyData[]; support legacy single object. */
  const totalRevenueRows = useMemo(() => {
    const tr = stats.totalRevenue;
    if (!tr) return [];
    return Array.isArray(tr) ? tr : [tr];
  }, [stats]);

  const restartStats = useMemo(() => {
    const d = restartStatsData?.getSimulatorRestartStatistics?.data;
    return d?.__typename === 'SimulatorRestartStatisticsData' ? d : {};
  }, [restartStatsData]);

  const restartRevenueRows = useMemo(() => {
    const revenue = restartStats.revenueByCurrency;
    if (!revenue) return [];
    return Array.isArray(revenue) ? revenue : [revenue];
  }, [restartStats]);

  const metricCards = useMemo(
    () => [
      // {
      //   title: 'Total Transactions',
      //   value: stats.totalTransactions ?? 0,
      //   subtitle: 'Specified period',
      //   icon: <SwapOutlined style={{fontSize: '24px', color: '#722ed1'}} />,
      //   iconBg: '#f9f0ff',
      // },
      {
        title: 'Premium Subscribers',
        value: stats.totalPremiumSubscribers ?? 0,
        subtitle: 'Specified period',
        icon: <UserOutlined style={{fontSize: '24px', color: '#1890ff'}} />,
        iconBg: '#e6f7ff',
      },
      {
        title: 'Active Premium Subscribers',
        value: stats.totalActivePremiumSubscribers ?? 0,
        subtitle: 'Specified period',
        icon: <UserAddOutlined style={{fontSize: '24px', color: '#52c41a'}} />,
        iconBg: '#f6ffed',
      },
      {
        title: 'Virtual Money Subscribers',
        value: stats?.totalVirtualMoneySubscribers ?? 0,
        subtitle: 'Specified period',
        icon: <DollarOutlined style={{fontSize: '24px', color: '#fa8c16'}} />,
        iconBg: '#fff7e6',
      },
    ],
    [stats],
  );

  const restartMetricCards = useMemo(
    () => [
      {
        title: 'Restart Users',
        value: restartStats.totalUsers ?? 0,
        subtitle: 'Specified period',
        icon: <UserOutlined style={{fontSize: '24px', color: '#1890ff'}} />,
        iconBg: '#e6f7ff',
      },
      {
        title: 'Total Restarts',
        value: restartStats.totalRestarts ?? 0,
        subtitle: 'Specified period',
        icon: <ReloadOutlined style={{fontSize: '24px', color: '#722ed1'}} />,
        iconBg: '#f9f0ff',
      },
    ],
    [restartStats],
  );

  const monthlyBreakdownData = useMemo(() => {
    const breakdown =
      monthlyData?.getSimulatorSubscriptionMonthlyBreakdown?.data?.breakdown ||
      [];
    const breakdownMap = breakdown.reduce((acc, item) => {
      if (item.month) {
        acc[item.month.toLowerCase()] = item.totalCount;
      }
      return acc;
    }, {});

    return moment.months().map(month => ({
      month,
      count: breakdownMap[month.toLowerCase()] || 0,
    }));
  }, [monthlyData]);

  if (!data && loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.filterSection}>
        <Form layout="inline" onFinish={onFinish} form={form}>
          <Form.Item label="Start Date" name="startDate">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item label="End Date" name="endDate">
            <DatePicker showTime />
          </Form.Item>
          <SubmitButton label="Apply" smallWidth />
        </Form>
      </div>
      <div
        style={{
          marginBottom: '16px',
          fontSize: '14px',
          color: '#595959',
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderRadius: '8px',
          border: '1px solid #91d5ff',
        }}>
        <strong>Note:</strong> Start and end dates apply to the summary cards,{' '}
        <strong>Total revenue</strong>, and{' '}
        <strong>Restart purchases statistics</strong>. The monthly breakdown
        below uses the selected year only, not this date range.
        <br />
        {appliedFilters?.startDate || appliedFilters?.endDate ? (
          <span>
            Currently showing filtered results from{' '}
            <strong>
              {appliedFilters?.startDate
                ? moment(appliedFilters.startDate).format('MMMM Do, YYYY')
                : moment().startOf('month').format('MMMM Do, YYYY')}
            </strong>{' '}
            to{' '}
            <strong>
              {appliedFilters?.endDate
                ? moment(appliedFilters.endDate).format('MMMM Do, YYYY')
                : moment().format('MMMM Do, YYYY')}
            </strong>
            .
          </span>
        ) : (
          <span>
            By default, showing results from{' '}
            <strong>{moment().startOf('month').format('MMMM Do, YYYY')}</strong>{' '}
            to <strong>{moment().format('MMMM Do, YYYY')}</strong>.
          </span>
        )}
      </div>
      <div className={styles.simulatorMetricsGrid}>
        {metricCards.map(item => (
          <Spin spinning={loading} key={item.title}>
            <div
              className={`${styles.metricOverviewCard} ${styles.filteredMetricCard}`}>
              <div
                className={styles.dabaProIconWrapper}
                style={{backgroundColor: item.iconBg}}>
                {item.icon}
              </div>
              <div className={styles.dabaProTextWrapper}>
                <span className={styles.dabaProValue}>
                  {Number(item.value).toLocaleString()}
                </span>
                <span className={styles.dabaProTitle}>{item.title}</span>
                <span className={styles.dabaProSubtitle}>{item.subtitle}</span>
              </div>
            </div>
          </Spin>
        ))}
      </div>
      <div className={styles.simulatorRevenueSection}>
        <div className={styles.simulatorRevenueSectionHeader}>
          <h3 className={styles.sectionTitle}>
            <DollarOutlined
              style={{marginRight: 8, color: '#13c2c2', fontSize: '20px'}}
            />
            TOTAL REVENUE
          </h3>
        </div>
        <Spin spinning={loading}>
          {totalRevenueRows.length > 0 ? (
            <div className={styles.simulatorRevenueGrid}>
              {totalRevenueRows.map((row, rowIdx) => (
                <div
                  key={`${row.currency || ''}-${rowIdx}`}
                  className={styles.simulatorRevenueCurrencyCard}>
                  <span className={styles.simulatorRevenueCurrencyAmount}>
                    {row.symbol || '$'}
                    {Number(row.totalAmount ?? 0).toLocaleString()}
                  </span>
                  <span className={styles.simulatorRevenueCurrencyCode}>
                    {row.currency || '—'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.simulatorRevenueEmpty}>
              No revenue data for this period
            </div>
          )}
        </Spin>
      </div>
      <div className={styles.simulatorRevenueSection}>
        <div className={styles.simulatorRevenueSectionHeader}>
          <h3 className={styles.sectionTitle}>
            <ReloadOutlined
              style={{marginRight: 8, color: '#722ed1', fontSize: '20px'}}
            />
            RESTART PURCHASES STATISTICS
          </h3>
        </div>
        <div className={styles.simulatorMetricsGrid}>
          {restartMetricCards.map(item => (
            <Spin spinning={restartStatsLoading} key={item.title}>
              <div
                className={`${styles.metricOverviewCard} ${styles.filteredMetricCard}`}>
                <div
                  className={styles.dabaProIconWrapper}
                  style={{backgroundColor: item.iconBg}}>
                  {item.icon}
                </div>
                <div className={styles.dabaProTextWrapper}>
                  <span className={styles.dabaProValue}>
                    {Number(item.value).toLocaleString()}
                  </span>
                  <span className={styles.dabaProTitle}>{item.title}</span>
                  <span className={styles.dabaProSubtitle}>
                    {item.subtitle}
                  </span>
                </div>
              </div>
            </Spin>
          ))}
        </div>
        <div className={styles.simulatorRevenueSection}>
          <div className={styles.simulatorRevenueSectionHeader}>
            <h3 className={styles.sectionTitle}>
              <DollarOutlined
                style={{marginRight: 8, color: '#13c2c2', fontSize: '20px'}}
              />
              RESTART REVENUE
            </h3>
          </div>
          <Spin spinning={restartStatsLoading}>
            {restartRevenueRows.length > 0 ? (
              <div className={styles.simulatorRevenueGrid}>
                {restartRevenueRows.map((row, rowIdx) => (
                  <div
                    key={`${row.currency || ''}-${rowIdx}`}
                    className={styles.simulatorRevenueCurrencyCard}>
                    <span className={styles.simulatorRevenueCurrencyAmount}>
                      {row.symbol || '$'}
                      {Number(row.totalAmount ?? 0).toLocaleString()}
                    </span>
                    <span className={styles.simulatorRevenueCurrencyCode}>
                      {`${row.currency || '—'} · ${Number(
                        row.totalRestarts ?? 0,
                      ).toLocaleString()} restarts`}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.simulatorRevenueEmpty}>
                No restart revenue data for this period
              </div>
            )}
          </Spin>
        </div>
      </div>
      <div className={styles.breakdownSection}>
        <div className={styles.breakdownHeader}>
          <h3 className={styles.sectionTitle}>
            {selectedYear.format('YYYY')} MONTHLY SUBSCRIPTION BREAKDOWN
          </h3>
          <div className={styles.yearFilter}>
            <span className={styles.filterLabel}>Filter Breakdown by Year</span>
            <DatePicker
              picker="year"
              allowClear={false}
              value={selectedYear}
              onChange={val => val && setSelectedYear(val)}
              className={styles.yearPicker}
            />
          </div>
        </div>
        {monthlyLoading ? (
          <div className={styles.loading} style={{height: '200px'}}>
            <Spin />
          </div>
        ) : (
          <div className={styles.monthlyBreakdownGrid}>
            {monthlyBreakdownData.map((item, index) => (
              <div key={index} className={styles.monthlyCard}>
                <span className={styles.monthLabel}>{item.month}</span>
                <span className={styles.monthValue}>{item.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <SimulatorSubscriptionsTable />
    </div>
  );
};
